import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Star,
  Clock,
  CheckCircle,
  XCircle,
  User,
  Mail,
  ImageIcon,
  MessageSquare,
  Calendar,
  Loader2,
  RefreshCw,
  Eye,
  Filter,
  ChevronDown,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;

const AdminCreatorRequestsPage = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [processingId, setProcessingId] = useState(null);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    total: 0,
  });

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = statusFilter !== "all" ? { status: statusFilter } : {};
      const response = await axios.get(`${API}/api/admin/creator-requests`, {
        params,
      });
      
      setRequests(response.data.requests || []);
      
      // Calculate stats from all requests (fetch without filter for stats)
      const allResponse = await axios.get(`${API}/api/admin/creator-requests`);
      const allRequests = allResponse.data.requests || [];
      
      setStats({
        pending: allRequests.filter(r => r.status === "pending").length,
        approved: allRequests.filter(r => r.status === "approved").length,
        rejected: allRequests.filter(r => r.status === "rejected").length,
        total: allRequests.length,
      });
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to fetch creator requests");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleApprove = async (requestId) => {
    setProcessingId(requestId);
    try {
      await axios.patch(
        `${API}/api/admin/creator-requests/${requestId}`,
        { status: "approved" }
      );
      fetchRequests();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to approve request");
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (requestId) => {
    setProcessingId(requestId);
    try {
      await axios.patch(
        `${API}/api/admin/creator-requests/${requestId}`,
        { status: "rejected" }
      );
      fetchRequests();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to reject request");
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: {
        icon: <Clock className="w-4 h-4" />,
        text: "Pending",
        className: "bg-amber-500/20 text-amber-400 border-amber-500/30",
      },
      approved: {
        icon: <CheckCircle className="w-4 h-4" />,
        text: "Approved",
        className: "bg-green-500/20 text-green-400 border-green-500/30",
      },
      rejected: {
        icon: <XCircle className="w-4 h-4" />,
        text: "Rejected",
        className: "bg-red-500/20 text-red-400 border-red-500/30",
      },
    };
    
    const badge = badges[status] || badges.pending;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${badge.className}`}>
        {badge.icon}
        {badge.text}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-xl border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/admin")}
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                data-testid="back-to-admin"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">Back to Dashboard</span>
              </button>
              <div className="h-6 w-px bg-white/20" />
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Star className="w-5 h-5 text-amber-400" />
                Creator Requests
              </h1>
            </div>
            
            <button
              onClick={fetchRequests}
              disabled={loading}
              className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              data-testid="refresh-requests"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/5 backdrop-blur-xl rounded-xl p-4 border border-white/10"
          >
            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-sm">Total Requests</span>
              <Star className="w-5 h-5 text-cyan-400" />
            </div>
            <p className="text-2xl font-bold text-white mt-2">{stats.total}</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-amber-500/10 backdrop-blur-xl rounded-xl p-4 border border-amber-500/20"
          >
            <div className="flex items-center justify-between">
              <span className="text-amber-400/80 text-sm">Pending</span>
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <p className="text-2xl font-bold text-amber-400 mt-2">{stats.pending}</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-green-500/10 backdrop-blur-xl rounded-xl p-4 border border-green-500/20"
          >
            <div className="flex items-center justify-between">
              <span className="text-green-400/80 text-sm">Approved</span>
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <p className="text-2xl font-bold text-green-400 mt-2">{stats.approved}</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-red-500/10 backdrop-blur-xl rounded-xl p-4 border border-red-500/20"
          >
            <div className="flex items-center justify-between">
              <span className="text-red-400/80 text-sm">Rejected</span>
              <XCircle className="w-5 h-5 text-red-400" />
            </div>
            <p className="text-2xl font-bold text-red-400 mt-2">{stats.rejected}</p>
          </motion.div>
        </div>

        {/* Filter Bar */}
        <div className="flex items-center justify-between mb-6">
          <div className="relative">
            <button
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white hover:bg-white/10 transition-colors"
              data-testid="filter-dropdown"
            >
              <Filter className="w-4 h-4" />
              <span className="capitalize">{statusFilter === "all" ? "All Requests" : statusFilter}</span>
              <ChevronDown className="w-4 h-4" />
            </button>
            
            {showFilterDropdown && (
              <div className="absolute top-full left-0 mt-2 w-48 bg-slate-800 border border-white/10 rounded-lg shadow-xl z-10">
                {["all", "pending", "approved", "rejected"].map((status) => (
                  <button
                    key={status}
                    onClick={() => {
                      setStatusFilter(status);
                      setShowFilterDropdown(false);
                    }}
                    className={`w-full px-4 py-2 text-left hover:bg-white/10 transition-colors capitalize ${
                      statusFilter === status ? "text-cyan-400" : "text-gray-300"
                    } ${status === "all" ? "rounded-t-lg" : ""} ${status === "rejected" ? "rounded-b-lg" : ""}`}
                    data-testid={`filter-${status}`}
                  >
                    {status === "all" ? "All Requests" : status}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <span className="text-gray-400 text-sm">
            Showing {requests.length} request{requests.length !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6 text-red-400">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-20">
            <Star className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Creator Requests</h3>
            <p className="text-gray-400">
              {statusFilter !== "all" 
                ? `No ${statusFilter} requests found.` 
                : "No creator requests have been submitted yet."}
            </p>
          </div>
        ) : (
          /* Requests List */
          <div className="space-y-4">
            <AnimatePresence>
              {requests.map((req, index) => (
                <motion.div
                  key={req.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 overflow-hidden hover:border-white/20 transition-colors"
                  data-testid={`request-card-${req.id}`}
                >
                  <div className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                      {/* User Avatar */}
                      <div className="flex-shrink-0">
                        {req.profile_image ? (
                          <img
                            src={req.profile_image}
                            alt={req.user_name}
                            className="w-16 h-16 rounded-full object-cover border-2 border-white/20"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center">
                            <User className="w-8 h-8 text-white" />
                          </div>
                        )}
                      </div>
                      
                      {/* User Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-2">
                          <h3 className="text-lg font-semibold text-white truncate">
                            {req.user_name}
                          </h3>
                          {getStatusBadge(req.status)}
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                          <div className="flex items-center gap-2 text-gray-400">
                            <Mail className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{req.user_email}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-400">
                            <ImageIcon className="w-4 h-4 flex-shrink-0" />
                            <span>{req.content_count} images</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-400">
                            <Calendar className="w-4 h-4 flex-shrink-0" />
                            <span>{formatDate(req.created_at)}</span>
                          </div>
                        </div>
                        
                        {/* Message */}
                        {req.message && (
                          <div className="mt-3 p-3 bg-white/5 rounded-lg">
                            <div className="flex items-start gap-2">
                              <MessageSquare className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                              <p className="text-gray-300 text-sm italic">"{req.message}"</p>
                            </div>
                          </div>
                        )}
                        
                        {/* Reviewed info */}
                        {req.reviewed_at && (
                          <p className="mt-2 text-xs text-gray-500">
                            Reviewed on {formatDate(req.reviewed_at)} by {req.reviewed_by}
                          </p>
                        )}
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <button
                          onClick={() => navigate(`/admin/fans/${req.user_id}`)}
                          className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
                          data-testid={`view-profile-${req.id}`}
                        >
                          <Eye className="w-4 h-4" />
                          <span className="hidden sm:inline">View Profile</span>
                        </button>
                        
                        {req.status === "pending" && (
                          <>
                            <button
                              onClick={() => handleReject(req.id)}
                              disabled={processingId === req.id}
                              className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors disabled:opacity-50"
                              data-testid={`reject-${req.id}`}
                            >
                              {processingId === req.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <XCircle className="w-4 h-4" />
                              )}
                              <span className="hidden sm:inline">Reject</span>
                            </button>
                            <button
                              onClick={() => handleApprove(req.id)}
                              disabled={processingId === req.id}
                              className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors disabled:opacity-50"
                              data-testid={`approve-${req.id}`}
                            >
                              {processingId === req.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <CheckCircle className="w-4 h-4" />
                              )}
                              <span className="hidden sm:inline">Approve</span>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminCreatorRequestsPage;
